import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup } from "@/components/ui/toggle-group";

const DEFAULT_HTML = `<div style="font-family: 'Georgia', serif; max-width: 800px; margin: 0 auto; padding: 20px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 12px; box-shadow: 0 10px 40px rgba(0,0,0,0.3);">
  <h1 style="color: #fff; text-align: center; font-size: 2.5em; margin-bottom: 10px; text-shadow: 2px 2px 4px rgba(0,0,0,0.3);">春江花月夜</h1>
  <p style="color: #f0f0f0; text-align: center; font-style: italic; margin-bottom: 30px; font-size: 1.1em;">唐·张若虚</p>
  
  <div style="background: rgba(255,255,255,0.95); padding: 30px; border-radius: 8px; line-height: 2; color: #333;">
    <p style="text-indent: 2em; margin-bottom: 1em; font-size: 1.1em;">春江潮水连海平，海上明月共潮生。</p>
    <p style="text-indent: 2em; margin-bottom: 1em; font-size: 1.1em; color: #d63447;">滟滟随波千万里，何处春江无月明！</p>
    <p style="text-indent: 2em; margin-bottom: 1em; font-size: 1.1em;">江流宛转绕芳甸，月照花林皆似霰。</p>
    <p style="text-indent: 2em; margin-bottom: 1em; font-size: 1.1em; color: #1e3799;">空里流霜不觉飞，汀上白沙看不见。</p>
    
    <div style="background: linear-gradient(90deg, #ffeaa7 0%, #fdcb6e 100%); padding: 20px; margin: 20px 0; border-left: 4px solid #e17055; border-radius: 4px;">
      <p style="text-indent: 2em; margin-bottom: 1em; font-size: 1.1em; color: #2d3436;">江天一色无纤尘，皎皎空中孤月轮。</p>
      <p style="text-indent: 2em; margin-bottom: 0; font-size: 1.1em; color: #2d3436;">江畔何人初见月？江月何年初照人？</p>
    </div>
    
    <p style="text-indent: 2em; margin-bottom: 1em; font-size: 1.1em;">人生代代无穷已，江月年年望相似。</p>
    <p style="text-indent: 2em; margin-bottom: 1em; font-size: 1.1em; color: #6c5ce7;">不知江月待何人，但见长江送流水。</p>
    <p style="text-indent: 2em; margin-bottom: 1em; font-size: 1.1em;">白云一片去悠悠，青枫浦上不胜愁。</p>
    <p style="text-indent: 2em; margin-bottom: 1em; font-size: 1.1em; color: #00b894;">谁家今夜扁舟子？何处相思明月楼？</p>
    
    <div style="background: rgba(108, 92, 231, 0.1); padding: 20px; margin: 20px 0; border-radius: 8px; border: 2px dashed #6c5ce7;">
      <p style="text-indent: 2em; margin-bottom: 1em; font-size: 1.1em; font-weight: bold; color: #6c5ce7;">可怜楼上月徘徊，应照离人妆镜台。</p>
      <p style="text-indent: 2em; margin-bottom: 1em; font-size: 1.1em;">玉户帘中卷不去，捣衣砧上拂还来。</p>
    </div>
    
    <p style="text-indent: 2em; margin-bottom: 1em; font-size: 1.1em;">此时相望不相闻，愿逐月华流照君。</p>
    <p style="text-indent: 2em; margin-bottom: 1em; font-size: 1.1em; color: #e74c3c;">鸿雁长飞光不度，鱼龙潜跃水成文。</p>
    <p style="text-indent: 2em; margin-bottom: 1em; font-size: 1.1em;">昨夜闲潭梦落花，可怜春半不还家。</p>
    <p style="text-indent: 2em; margin-bottom: 1em; font-size: 1.1em; color: #fd79a8;">江水流春去欲尽，江潭落月复西斜。</p>
    <p style="text-indent: 2em; margin-bottom: 1em; font-size: 1.1em;">斜月沉沉藏海雾，碣石潇湘无限路。</p>
    <p style="text-indent: 2em; font-size: 1.2em; font-weight: bold; color: #2d3436; text-align: center; margin-top: 20px;">不知乘月几人归，落月摇情满江树。</p>
  </div>
  
  <div style="text-align: center; margin-top: 20px; padding: 15px; background: rgba(255,255,255,0.2); border-radius: 8px;">
    <p style="color: #fff; font-size: 0.9em; margin: 0;">✨ 这是一段富含内联样式的 HTML 示例 ✨</p>
  </div>
</div>`;

type DisplayMode = "both" | "stream" | "single";

const DURATION_OPTIONS = [
  { value: 5, label: "5 秒" },
  { value: 15, label: "15 秒" },
  { value: 30, label: "30 秒" },
  { value: 45, label: "45 秒" },
  { value: 60, label: "60 秒" },
];

export function StreamingShowcase() {
  const [durationSeconds, setDurationSeconds] = useState(5);
  const [htmlContent, setHtmlContent] = useState(DEFAULT_HTML);
  const [isHtmlExpanded, setIsHtmlExpanded] = useState(false);
  const [displayMode, setDisplayMode] = useState<DisplayMode>("both");
  const [isFullscreen, setIsFullscreen] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [singleShotText, setSingleShotText] = useState("");
  const [streamingTime, setStreamingTime] = useState<number | null>(null);
  const [singleShotTime, setSingleShotTime] = useState<number | null>(null);
  const [streamMeta, setStreamMeta] = useState<{ 
    chunkCount: number | null; 
    chunkSize: number | null;
    delayMs: number | null;
  }>({
    chunkCount: null,
    chunkSize: null,
    delayMs: null,
  });
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const durationMs = useMemo(() => Math.round(durationSeconds * 1000), [durationSeconds]);
  const htmlLength = htmlContent.length;
  const progressStream = htmlLength ? Math.min(1, streamingText.length / htmlLength) : 0;
  const progressSingle = htmlLength ? Math.min(1, singleShotText.length / htmlLength) : 0;

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const stopDemo = () => {
    abortRef.current?.abort();
    setIsRunning(false);
  };

  const startDemo = async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsRunning(true);
    setError(null);
    setStreamingText("");
    setSingleShotText("");
    setStreamingTime(null);
    setSingleShotTime(null);
    setStreamMeta({ chunkCount: null, chunkSize: null, delayMs: null });

    try {
      const promises = [];
      if (displayMode === "both" || displayMode === "stream") {
        promises.push(runStreamingRequest(durationMs, htmlContent, controller.signal, setStreamingText, setStreamingTime, setStreamMeta));
      }
      if (displayMode === "both" || displayMode === "single") {
        promises.push(runSingleShotRequest(durationMs, htmlContent, controller.signal, setSingleShotText, setSingleShotTime));
      }
      await Promise.all(promises);
    } catch (err) {
      if ((err as DOMException)?.name === "AbortError") {
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (!controller.signal.aborted) {
        setIsRunning(false);
      }
    }
  };

  const shouldShowStream = displayMode === "both" || displayMode === "stream";
  const shouldShowSingle = displayMode === "both" || displayMode === "single";

  return (
    <section className={`space-y-8 transition-all ${isFullscreen ? "fixed inset-0 z-50 bg-background p-6 overflow-auto" : ""}`}>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">流式与非流式对比</h2>
        <Button variant="outline" onClick={() => setIsFullscreen(!isFullscreen)}>
          {isFullscreen ? "退出全屏" : "进入全屏"}
        </Button>
      </div>
      
      <div className="rounded-3xl border border-border/60 bg-card/70 p-6 shadow-xl shadow-black/5 backdrop-blur">
        <div className="space-y-6">
          <div className="space-y-2 min-h-[60px]">
            <div className="flex items-center justify-between">
              <Label htmlFor="htmlContent">HTML 内容</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsHtmlExpanded(!isHtmlExpanded)}
                className="text-xs"
              >
                {isHtmlExpanded ? "收起" : "展开"}
              </Button>
            </div>
            {isHtmlExpanded && (
              <>
                <Textarea
                  id="htmlContent"
                  value={htmlContent}
                  onChange={event => setHtmlContent(event.currentTarget.value)}
                  className="font-mono text-sm min-h-[200px]"
                  placeholder="输入要测试的 HTML 内容..."
                  disabled={isRunning}
                />
                <p className="text-sm text-muted-foreground">
                  在这里输入或修改 HTML 内容，将用于流式和非流式对比测试。
                </p>
              </>
            )}
            {!isHtmlExpanded && (
              <p className="text-sm text-muted-foreground">
                已加载默认 HTML 内容（{htmlLength} 字符），点击"展开"查看或编辑。
              </p>
            )}
          </div>
          
          <div className="space-y-4">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label>输出总耗时</Label>
                <ToggleGroup
                  value={String(durationSeconds)}
                  onValueChange={(v) => setDurationSeconds(Number(v))}
                  options={DURATION_OPTIONS.map(opt => ({ value: String(opt.value), label: opt.label }))}
                  disabled={isRunning}
                  className="w-full justify-start flex-wrap"
                />
              </div>
              
              <div className="space-y-2">
                <Label>显示模式</Label>
                <ToggleGroup
                  value={displayMode}
                  onValueChange={(v) => setDisplayMode(v as DisplayMode)}
                  options={[
                    { value: "both" as const, label: "流式 + 非流式" },
                    { value: "stream" as const, label: "仅流式" },
                    { value: "single" as const, label: "仅非流式" },
                  ]}
                  disabled={isRunning}
                  className="w-full justify-start flex-wrap"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button onClick={startDemo} disabled={isRunning} className="flex-1" size="lg">
                {isRunning ? "运行中..." : "开始对比"}
              </Button>
              <Button type="button" variant="secondary" onClick={stopDemo} disabled={!isRunning} size="lg">
                停止
              </Button>
            </div>
          </div>
        </div>
        {error && <p className="mt-4 rounded-xl border border-destructive/60 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</p>}
      </div>

      <div className={`grid gap-6 ${displayMode === "both" ? "lg:grid-cols-2" : "lg:grid-cols-1"}`}>
        {shouldShowStream && (
          <DemoPanel
            title="流式输出"
            description="通过 ReadableStream 分批推送 chunk，观察内容逐渐填满的过程。"
            progress={progressStream}
            elapsedMs={streamingTime}
            meta={[
              { label: "Chunk 数量", value: formatOptionalNumber(streamMeta.chunkCount) },
              { label: "Chunk 大小", value: streamMeta.chunkSize ? `${streamMeta.chunkSize} 字符` : "—" },
              { label: "发送间隔", value: streamMeta.delayMs !== null ? formatMillis(streamMeta.delayMs) : "—" },
            ]}
            content={streamingText}
            isLoading={isRunning}
          />
        )}
        {shouldShowSingle && (
          <DemoPanel
            title="非流式输出"
            description="等待后端准备完整 HTML，再一次性返回全部内容。"
            progress={progressSingle}
            elapsedMs={singleShotTime}
            meta={[
              { label: "Chunk 数量", value: "1" }, 
              { label: "Chunk 大小", value: `${htmlLength} 字符` }
            ]}
            content={singleShotText}
            isLoading={isRunning}
          />
        )}
      </div>
    </section>
  );
}

type MetaInfo = { label: string; value: string };

function DemoPanel({
  title,
  description,
  progress,
  elapsedMs,
  meta,
  content,
  isLoading,
}: {
  title: string;
  description: string;
  progress: number;
  elapsedMs: number | null;
  meta: MetaInfo[];
  content: string;
  isLoading: boolean;
}) {
  return (
    <div className="rounded-3xl border border-border/60 bg-card/80 p-6 shadow-lg shadow-black/5 backdrop-blur flex flex-col">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
        <div className="flex-1">
          <h3 className="text-xl font-semibold tracking-wide">{title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        </div>
        <span className="rounded-full border border-border/70 px-3 py-1 text-xs font-medium tracking-[0.3em] text-muted-foreground whitespace-nowrap self-start">
          {elapsedMs == null ? "N/A" : formatMillis(elapsedMs)}
        </span>
      </div>

      <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-muted">
        <span
          className="block h-full bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 transition-all"
          style={{ width: `${progress * 100}%` }}
        />
      </div>

      <dl className={`mt-4 grid gap-3 text-sm ${meta.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
        {meta.map(item => (
          <div key={item.label} className="rounded-2xl border border-border/70 bg-background/60 p-3">
            <dt className="text-xs text-muted-foreground mb-1">{item.label}</dt>
            <dd className="text-base font-semibold text-foreground">{item.value}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-6 rounded-2xl border border-border/70 bg-background/95 p-4 overflow-auto max-h-[600px] flex-1">
        {content ? (
          <div dangerouslySetInnerHTML={{ __html: content }} />
        ) : isLoading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-6">
            <div className="relative w-20 h-20">
              <div className="absolute inset-0 rounded-full border-4 border-primary/30"></div>
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary animate-spin"></div>
              <div className="absolute inset-2 rounded-full border-4 border-transparent border-t-purple-500 animate-spin" style={{ animationDuration: "1.5s", animationDirection: "reverse" }}></div>
              <div className="absolute inset-4 rounded-full border-4 border-transparent border-t-pink-500 animate-spin" style={{ animationDuration: "2s" }}></div>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-2 h-2 rounded-full bg-primary animate-pulse" style={{ animationDelay: "0ms" }}></div>
                <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" style={{ animationDelay: "200ms" }}></div>
                <div className="w-2 h-2 rounded-full bg-pink-500 animate-pulse" style={{ animationDelay: "400ms" }}></div>
              </div>
              <p className="text-sm text-muted-foreground animate-pulse">加载中...</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center py-16">
            <p className="text-sm text-muted-foreground">准备就绪，点击"开始对比"查看效果</p>
          </div>
        )}
      </div>
    </div>
  );
}

async function runStreamingRequest(
  durationMs: number,
  htmlContent: string,
  signal: AbortSignal,
  setText: (text: string) => void,
  setElapsed: (value: number) => void,
  setMeta: (value: { chunkCount: number | null; chunkSize: number | null; delayMs: number | null }) => void,
) {
  // 前端模拟流式输出
  const BASE_INTERVAL_MS = 140;
  const MIN_CHUNK_SIZE = 32;
  
  // 先计算理论的 chunk 数量和大小
  const targetChunkCount = Math.max(1, Math.round(durationMs / BASE_INTERVAL_MS));
  const calculatedChunkSize = Math.ceil(htmlContent.length / targetChunkCount);
  const chunkSize = Math.max(MIN_CHUNK_SIZE, calculatedChunkSize);
  
  // 基于实际的 chunkSize 计算真实的 chunk 数量
  const actualChunkCount = Math.ceil(htmlContent.length / chunkSize);
  
  // 基于实际 chunk 数量计算每个 chunk 之间的延迟（关键：动态调整速率）
  const delayMs = durationMs > 0 && actualChunkCount > 1 
    ? Math.round(durationMs / (actualChunkCount - 1)) 
    : 0;

  setMeta({
    chunkCount: actualChunkCount,
    chunkSize,
    delayMs,
  });

  const start = performance.now();
  let aggregated = "";

  for (let i = 0; i < htmlContent.length; i += chunkSize) {
    if (signal.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }
    
    const chunk = htmlContent.slice(i, i + chunkSize);
    aggregated += chunk;
    setText(aggregated);
    
    // 除了最后一个 chunk，都需要延迟
    if (delayMs > 0 && i + chunkSize < htmlContent.length) {
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }

  setElapsed(performance.now() - start);
}

async function runSingleShotRequest(
  durationMs: number,
  htmlContent: string,
  signal: AbortSignal,
  setText: (text: string) => void,
  setElapsed: (value: number) => void,
) {
  // 前端模拟非流式输出
  const start = performance.now();
  
  if (durationMs > 0) {
    await new Promise((resolve, reject) => {
      const timeout = setTimeout(resolve, durationMs);
      signal.addEventListener("abort", () => {
        clearTimeout(timeout);
        reject(new DOMException("Aborted", "AbortError"));
      });
    });
  }
  
  if (signal.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }

  setText(htmlContent);
  setElapsed(performance.now() - start);
}

function formatMillis(value: number) {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(2)}s`;
  }
  return `${Math.round(value)}ms`;
}

function formatOptionalNumber(value: number | null) {
  if (value == null) return "—";
  return value.toString();
}

